import React, { useContext, useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  Divider,
  Form,
  Input,
  message,
  Col,
  Row,
  Tooltip,
  Modal,
} from 'antd';
import Table from '@components/Table';
import { useTranslation } from 'react-i18next';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import api from '@services/api';
import useSwr from '@hooks/useSwr';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';

import * as S from './styles';

const { Item } = Form;
const { confirm: ConfirmModal } = Modal;

const MyUser = () => {
  const { t } = useTranslation(['users', 'attributes']);
  const { keycloak } = useContext(KeycloakContext);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const { data: otpData, mutate } = useSwr(`/users/self/credentials/otp`);

  const hasOtp = otpData && otpData.length > 0;

  const updateUser = useCallback(
    (id: any, updUser: any) => api.put(`/users/${id}`, updUser),
    [],
  );

  useEffect(() => {
    keycloak
      .loadUserProfile()
      .then((fields: any) => {
        if (fields.attributes.cpfCnpj?.length > 0) {
          // eslint-disable-next-line prefer-destructuring, no-param-reassign
          fields.attributes.cpfCnpj = fields.attributes.cpfCnpj[0];
        }
        if (fields.attributes.phoneNumber?.length > 0) {
          // eslint-disable-next-line no-param-reassign
          [fields.attributes.phoneNumber] = fields.attributes.phoneNumber;
        }
        form.setFieldsValue(fields);
      })
      .catch(() => message.error(t('msgs.error-finding-profile')))
      .finally(() => setLoading(false));
  }, [keycloak, form, t]);

  const handleEnable2FA = useCallback(async () => {
    try {
      await api.post(`users/self/credentials/otp`);
      message.info(t('msgs.redirecting-to-2fa-setup'));
      window.location.reload();
    } catch (error) {
      message.error(t('error-2fa-setup'));
    }
  }, [t]);

  const onFinish = useCallback(
    async (values: any) => {
      setSaveLoading(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { password, confirm, ...newValues } = values;
        const payload = {
          ...newValues,
        };

        if (password) {
          payload.credentials = [{ value: password }];
        }
        await updateUser(keycloak.subject, payload);
        message.success(t('msgs.success-updating-user'));
      } catch {
        message.error(t('msgs.error-updating-user'));
      }
      setSaveLoading(false);
    },
    [keycloak.subject, t, updateUser],
  );

  const onDelete = useCallback(
    async (idToDelete: string) => {
      try {
        await api.delete(`/users/self/credentials/${idToDelete}`);
        mutate({
          ...otpData,
          data: otpData.filter((c: any) => c.id !== idToDelete),
        });
        message.success(t('msgs.success-deleting-device'));
      } catch {
        message.error(t('msgs.error-deleting-device'));
      }
    },
    [t, otpData, mutate],
  );

  const showDelConfirm = (deviceName: string, idToDelete: string) => {
    ConfirmModal({
      title: <h3>{t('my-user.delete', { deviceName })}</h3>,
      icon: <ExclamationCircleOutlined />,
      content: t('msgs.deleteAlert'),
      okText: t('table.yes'),
      cancelText: t('table.no'),
      okButtonProps: { type: 'primary', danger: true },
      afterClose: () => document.body.style.removeProperty('overflow'),
      onOk: () => onDelete(idToDelete),
    });
  };

  const showConfirmTwoAuth = () => {
    ConfirmModal({
      title: <h3>{t('modal.titles.two-factor')}</h3>,
      icon: <ExclamationCircleOutlined />,
      content: t('buttons.info2fa'),
      okText: t('table.yes'),
      cancelText: t('table.no'),
      okButtonProps: { type: 'primary', danger: true },
      afterClose: () => document.body.style.removeProperty('overflow'),
      onOk: () => handleEnable2FA(),
    });
  };

  const columns = [
    {
      title: t('table.type'),
      dataIndex: 'type',
      key: 'type',
      align: 'center',
    },
    {
      title: t('table.userLabel'),
      dataIndex: 'userLabel',
      key: 'userLabel',
      align: 'center',
    },
    {
      title: t('table.temporary'),
      dataIndex: 'temporary',
      key: 'temporary',
      render: (temporary: boolean) =>
        temporary ? t('table.yes') : t('table.no'),
      align: 'center',
    },
    {
      title: t('attributes:actions'),
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      width: 100,
      render: (id: string, record: any) => (
        <Tooltip title={t('buttons.deleteButton')}>
          <Button
            style={{ padding: 0, margin: '0 6px' }}
            type="link"
            danger
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              showDelConfirm(record.userLabel, id);
            }}
          >
            <DeleteOutlined />
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <S.CustomCardContainer
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {t('my-user.title')}
          {!hasOtp && (
            <Button onClick={showConfirmTwoAuth} type="primary">
              {t('buttons.enable2FA')}
            </Button>
          )}
        </div>
      }
    >
      <Skeleton loading={loading} active>
        <Form
          colon={false}
          form={form}
          id="userForm"
          style={{ width: '100%' }}
          scrollToFirstError
          onFinish={onFinish}
          layout="vertical"
        >
          <Row gutter={[16, 16]} justify="center">
            <Col lg={12} xl={8} xs={24}>
              <Item
                label={t('form.firstName')}
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: t('form.msgs.fillThisField'),
                  },
                ]}
              >
                <Input />
              </Item>
            </Col>

            <Col lg={12} xl={8} xs={24}>
              <Item
                label={t('form.lastName')}
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: t('form.msgs.fillThisField'),
                  },
                ]}
              >
                <Input />
              </Item>
            </Col>

            <Col lg={12} xl={8} xs={24}>
              <Item
                label={t('form.email')}
                name="email"
                rules={[
                  {
                    required: true,
                    message: t('form.msgs.fillThisField'),
                  },
                  {
                    type: 'email',
                    message: t('form.msgs.notValidEmail'),
                  },
                ]}
              >
                <Input />
              </Item>
            </Col>

            <Col lg={12} xl={8} xs={24}>
              <Item label={t('form.username')} name="username">
                <Input disabled />
              </Item>
            </Col>

            <Col lg={12} xl={8} xs={24}>
              <Item
                label={t('form.cpfCnpj')}
                name={['attributes', 'cpfCnpj']}
                normalize={value => (value ? value.replace(/\D/g, '') : '')}
              >
                <Input maxLength={30} />
              </Item>
            </Col>

            <Col lg={12} xl={8} xs={24}>
              <Item
                label={t('form.phoneNumber')}
                name={['attributes', 'phoneNumber']}
              >
                <Input />
              </Item>
            </Col>

            <Divider style={{ margin: 0, marginBottom: 16 }} />

            <Col lg={12} xl={8} xs={24}>
              <Item
                label={t('form.password')}
                name="password"
                rules={[
                  {
                    required: false,
                    message: t('form.msgs.fillThisField'),
                  },
                  {
                    min: 10,
                    message: t('form.msgs.passwordLength', {
                      minLength: 10,
                    }),
                  },
                ]}
              >
                <Input.Password />
              </Item>
            </Col>

            <Col lg={12} xl={8} xs={24}>
              <Item
                dependencies={['password']}
                label={t('form.confirmPassword')}
                name="confirm"
                rules={[
                  {
                    required: (form.getFieldValue('password') || '').length > 0,
                    message: t('form.msgs.passwordConfirmRequired'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(t('form.msgs.passwordDoesNotMatch')),
                      );
                    },
                  }),
                ]}
                hasFeedback
              >
                <Input.Password />
              </Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'right' }}>
            <Divider style={{ marginBottom: 16 }} />
            <Button type="primary" htmlType="submit" loading={saveLoading}>
              {t('buttons.saveButton')}
            </Button>
          </div>

          {hasOtp && (
            <>
              <Divider />
              <Table
                // @ts-ignore
                columns={columns}
                data={otpData || []}
                loading={!otpData}
                tableKey="groups"
                title={t('my-user.auth')}
                extraActions={
                  <Tooltip title={t('buttons.addDevice')}>
                    <Button
                      icon={<PlusCircleOutlined />}
                      type="link"
                      size="large"
                      onClick={showConfirmTwoAuth}
                    />
                  </Tooltip>
                }
              />
            </>
          )}
        </Form>
      </Skeleton>
    </S.CustomCardContainer>
  );
};

export default MyUser;
